import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "../../interfaces/DecisionPriority";
import {
  Board,
  Move,
  IPiece,
  Position,
  PieceType,
  MoveType,
} from "../../../../types";
import {
  MCTSUtils_v2,
  cloneFullGameState,
  getPieceFromPosition,
  applyMoveWithSnakeRespawn,
} from "../../../utils/MCTSUtils";
import { MCTSMoveGenerationService } from "../../../utils/MCTSMoveGenerationService";

/**
 * Priority 4: Don't Setup Race
 *
 * This is distinct from Race Block as Race block checks if the AI can MOVE an uncapturable piece on its immediate turn to block a single option for a race victory
 * Don't setup race's only priority is checking if MOVING a piece newly ALLOWS the enemy to IMMEDITATELY complete a race victory!
 * This priority limits and removes moves that enable immediate wins for the enemy!
 */
export class DontSetupRacePriority extends BaseDecisionPriority {
  readonly priority = 4;
  readonly name = "Don't Setup Race";
  readonly description =
    "CRITICAL: Prevent moves that enable enemy race victories on their next turn";

  private analysisLogs: string[] = [];
  private suspiciousMoveLogged = false;

  isRelevant(context: DecisionContext): boolean {
    // Always relevant - we must always check if our moves enable enemy race wins
    return true;
  }

  analyze(context: DecisionContext): DecisionResult {
    this.analysisLogs = [];
    this.suspiciousMoveLogged = false; // Reset for each analysis

    const gameState = context.gameState;
    const isPlayerTurn = gameState.isLightTurn;
    const playerColor = isPlayerTurn ? "Light" : "Dark";
    const enemyColor = isPlayerTurn ? "Dark" : "Light";

    // DEBUG: Log game state and moves using proper logging
    this.addLog(
      `🎮 Game State: ${playerColor} turn ${gameState.turnCount || "?"}`
    );

    // Board state validation and logging
    let boardValid = true;
    const pieceCount = { Light: 0, Dark: 0 };

    for (let row = 0; row < 8; row++) {
      if (!gameState.board || !gameState.board[row]) {
        this.addLog(`🚨 Invalid board structure at row ${row}`);
        boardValid = false;
        break;
      }
      for (let col = 0; col < 8; col++) {
        if (!gameState.board[row][col]) {
          this.addLog(`🚨 Invalid board square at [${row},${col}]`);
          boardValid = false;
          break;
        }
        const square = gameState.board[row][col];
        if (square && square.Pieces && square.Pieces.length > 0) {
          const piece = square.Pieces[0];
          pieceCount[piece.isLight ? "Light" : "Dark"]++;
        }
      }
      if (!boardValid) break;
    }

    this.addLog(
      `📊 Board valid: ${boardValid}, Pieces: Light ${pieceCount.Light}, Dark ${pieceCount.Dark}`
    );

    // Log sample moves
    this.addLog(
      `🎯 Sample moves (first 3 of ${context.allPossibleMoves.length}):`
    );
    for (let i = 0; i < Math.min(context.allPossibleMoves.length, 3); i++) {
      const move = context.allPossibleMoves[i];
      this.addLog(
        `   ${i + 1}. [${move.fromPosition.row},${move.fromPosition.col}] → [${
          move.move.position.row
        },${move.move.position.col}]`
      );
    }

    // Find moves that enable enemy race victories
    const dangerousMoves: MoveInfo[] = [];
    const safeMoves: MoveInfo[] = [];

    this.addLog(
      `🔍 Analyzing ${context.allPossibleMoves.length} moves for race enablement`
    );

    for (const moveInfo of context.allPossibleMoves) {
      const enablesRace = this.doesMoveEnableEnemyRaceVictory(
        moveInfo,
        gameState
      );

      if (enablesRace.enables) {
        dangerousMoves.push(moveInfo);
      } else {
        safeMoves.push(moveInfo);
      }
    }

    if (dangerousMoves.length === 0) {
      this.addLog(`✅ No race-enabling moves found`);
      return {
        shouldEarlyExit: false,
        reasoning: "No moves enable enemy race victories",
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    if (safeMoves.length === 0) {
      this.addLog(
        `💀 All ${dangerousMoves.length} moves potentially enable enemy race - deferring to MCTS for deeper analysis.`,
        "IMPROVE"
      ); // Tagged for feedback loop
      this.addLog(`🚨 FILTERED MOVES SUMMARY:`);
      for (let i = 0; i < Math.min(dangerousMoves.length, 5); i++) {
        const move = dangerousMoves[i];
        this.addLog(
          `   ${i + 1}. [${move.fromPosition.row},${
            move.fromPosition.col
          }] → [${move.move.position.row},${
            move.move.position.col
          }] - Race enabler`
        );
      }
      if (dangerousMoves.length > 5) {
        this.addLog(`   ... and ${dangerousMoves.length - 5} more moves`);
      }

      // FIXED: No early exit - pass all moves to MCTS with warning
      return {
        shouldEarlyExit: false,
        reasoning:
          "Position critical: All moves may enable race - defer to MCTS",
        priority: this.priority,
        filteredMoves: context.allPossibleMoves, // Pass unfiltered
        diagnostics: {
          logs: this.analysisLogs,
          dangerousMoves: dangerousMoves.length,
          safeMoves: 0,
        },
      };
    }

    // Success case - filtered out dangerous moves
    this.addLog(
      `🛡️ Filtered out ${dangerousMoves.length} race-enabling moves, kept ${safeMoves.length} safe moves`
    );
    this.addLog(`🚨 FILTERED MOVES SUMMARY:`);
    for (let i = 0; i < Math.min(dangerousMoves.length, 3); i++) {
      const move = dangerousMoves[i];
      this.addLog(
        `   ❌ [${move.fromPosition.row},${move.fromPosition.col}] → [${move.move.position.row},${move.move.position.col}] - Race enabler`
      );
    }
    if (dangerousMoves.length > 3) {
      this.addLog(
        `   ... and ${dangerousMoves.length - 3} more dangerous moves`
      );
    }

    return this.createFilterResult(
      safeMoves,
      `DON'T SETUP RACE: Filtered out ${dangerousMoves.length} race-enabling moves`,
      {
        dangerousMoves: dangerousMoves.length,
        safeMoves: safeMoves.length,
        logs: this.analysisLogs,
      }
    );
  }

  private addLog(message: string, tag?: "IMPROVE" | "FEEDBACK"): void {
    const prefixed = tag ? `[${tag}: ${message}]` : message;
    this.analysisLogs.push(prefixed);
  }

  /**
   * Check if a move enables the enemy to achieve a race victory on their next turn
   * 2-ply analysis: simulate our move, then check all enemy responses for race wins
   * FIX: Add RNG sampling for stochastic pieces (e.g., Monkey capture prob)
   * FIX: Focus on fast racing kings only
   */
  private doesMoveEnableEnemyRaceVictory(
    moveInfo: MoveInfo,
    gameState: any
  ): { enables: boolean; reason: string } {
    // DEBUG: Add logging for this specific move
    const debugMove = `[${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}] → [${moveInfo.move.position.row},${moveInfo.move.position.col}]`;

    try {
      const originalPlayerIsLight = gameState.isLightTurn;
      const enemyVictoryRow = originalPlayerIsLight ? 0 : 7; // Enemy's victory row

      // DEBUG: Log for suspicious moves (first occurrence only)
      if (
        moveInfo.fromPosition.row === 0 &&
        moveInfo.fromPosition.col === 4 &&
        !this.suspiciousMoveLogged
      ) {
        this.addLog(`🔍 Analyzing suspicious move: ${debugMove}`);
        this.addLog(
          `  - Current player is light: ${originalPlayerIsLight}, Enemy victory row: ${enemyVictoryRow}`
        );
        this.suspiciousMoveLogged = true;
      }

      // Step 1: Clone the game state and simulate our move
      const clonedState = cloneFullGameState(gameState);

      // Get the piece we're moving
      const piece = getPieceFromPosition(clonedState, moveInfo.fromPosition);
      if (!piece) {
        return { enables: false, reason: "No piece found at move position" };
      }

      // Apply our move to the cloned state
      applyMoveWithSnakeRespawn(
        clonedState,
        moveInfo.fromPosition,
        moveInfo.move,
        piece
      );

      // Step 2: Check if enemy can now achieve race victory
      // The turn should now be the enemy's after our move
      const moveService = MCTSMoveGenerationService.getInstance();

      // Generate all possible enemy moves after our move
      const enemyMoves = moveService.getAllMoves(clonedState);

      // Find enemy king position
      const enemyIsLight = !originalPlayerIsLight;
      const enemyKingPosition = this.findKingInBoard(
        clonedState.board,
        enemyIsLight
      );

      if (!enemyKingPosition) {
        return { enables: false, reason: "Enemy king not found" };
      }

      // Check if any enemy king move reaches their victory row
      const enemyKingMoves = enemyMoves.filter(
        (move) =>
          move.fromPosition.row === enemyKingPosition.row &&
          move.fromPosition.col === enemyKingPosition.col
      );

      for (const enemyMove of enemyKingMoves) {
        if (enemyMove.move.position.row === enemyVictoryRow) {
          return {
            enables: true,
            reason: `Enemy king can reach victory row ${enemyVictoryRow} from ${enemyKingPosition.row},${enemyKingPosition.col} to ${enemyMove.move.position.row},${enemyMove.move.position.col}`,
          };
        }
      }

      return { enables: false, reason: "No enemy race victory possible" };
    } catch (error) {
      this.addLog(`❌ Error checking move ${debugMove}: ${error}`);
      return { enables: false, reason: `Simulation error: ${error}` };
    }
  }

  /**
   * Find king position in a board
   */
  private findKingInBoard(board: any[][], isLight: boolean): Position | null {
    for (let row = 0; row < board.length; row++) {
      for (let col = 0; col < board[row].length; col++) {
        const square = board[row][col];
        if (square && square.Pieces && square.Pieces.length > 0) {
          const piece = square.Pieces[0];
          if (piece.isKing && piece.isLight === isLight) {
            return { row, col };
          }
        }
      }
    }
    return null;
  }
}

